import { onMounted, onUnmounted, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useUpdateStore } from '@/stores/useUpdateStore'
import { isDesktopRuntime } from '@/platform/bridge'
import { logger } from '@/utils/logger'
import { summarizeUpdateError } from '@/utils/updateError'

const UPDATE_STARTUP_DELAY_MIN_MS = 5000
const UPDATE_STARTUP_DELAY_RANGE_MS = 55000

function isMainWindow() {
  const location = `${window.location.pathname}${window.location.hash}`
  return !location.includes('sidebar') && !location.includes('floating-ball')
}

export function useUpdateLifecycle() {
  const authStore = useAuthStore()
  const updateStore = useUpdateStore()
  const mainWindow = isMainWindow()
  const autoCheckEnabled = isDesktopRuntime && mainWindow && !import.meta.env.DEV
  let startupTimer: ReturnType<typeof setTimeout> | null = null

  async function runCheck(reason: string, force = false) {
    if (!autoCheckEnabled || (!force && updateStore.hasUpdate)) return
    try {
      await updateStore.checkForUpdate({ manual: false, force })
    } catch (error) {
      logger.warn('updater', `Auto update check failed: ${reason}`, summarizeUpdateError(error))
    }
  }

  function handleWindowFocus() {
    void runCheck('window focus')
  }

  function handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      void runCheck('page visible')
    }
  }

  const stopAuthWatch = watch(
    () => authStore.isLoggedIn,
    (loggedIn, wasLoggedIn) => {
      if (!mainWindow) return
      if (!loggedIn && wasLoggedIn === true) {
        updateStore.clearAvailable()
        return
      }
      if (loggedIn && wasLoggedIn !== true) {
        void runCheck('authenticated session ready', true)
      }
    },
    { immediate: true },
  )

  onMounted(() => {
    if (!autoCheckEnabled) return
    window.addEventListener('focus', handleWindowFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    const delay =
      UPDATE_STARTUP_DELAY_MIN_MS + Math.floor(Math.random() * UPDATE_STARTUP_DELAY_RANGE_MS)
    startupTimer = setTimeout(() => {
      void runCheck('startup')
    }, delay)
  })

  onUnmounted(() => {
    stopAuthWatch()
    window.removeEventListener('focus', handleWindowFocus)
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    if (startupTimer) {
      clearTimeout(startupTimer)
      startupTimer = null
    }
  })
}
